// PDP redesign behaviours. One file, no deps. Modules init only if their DOM exists.
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const money = (cents) =>
    new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(cents / 100).replace(/\.00$/, "");

  // Show the gallery image that belongs to a variant. media_id comes from the buy box's
  // variant JSON (0 = variant has no image of its own, so the gallery is left alone).
  function showVariantMedia(mediaId) {
    if (!mediaId) return;
    const thumb = $(`[data-thumb][data-media="pdp-media-${mediaId}"]`);
    if (!thumb) return;
    thumb.click(); // reuses the hero swap and active-thumb state
    if (thumb.classList.contains("pdp-thumb--overflow")) $("[data-thumbs]")?.classList.add("is-expanded");
    const track = $("[data-mob-gallery]");
    const i = $$("[data-thumb]").indexOf(thumb);
    if (track && i > -1) track.scrollTo({ left: i * track.clientWidth, behavior: "smooth" });
  }

  function initGallery() {
    const hero = $("[data-hero]");
    if (hero) {
      $$("[data-thumb]").forEach((t) =>
        t.addEventListener("click", () => {
          const tpl = document.getElementById(t.dataset.media);
          if (tpl) hero.innerHTML = tpl.innerHTML;
          $$("[data-thumb]").forEach((x) => x.classList.toggle("is-active", x === t));
        })
      );
      $("[data-thumbs-more]")?.addEventListener("click", () => $("[data-thumbs]").classList.add("is-expanded"));
    }
    const track = $("[data-mob-gallery]");
    const dots = $$("[data-dots] .pdp-dot");
    if (track && dots.length) {
      track.addEventListener("scroll", () => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        dots.forEach((d, j) => d.classList.toggle("is-active", j === i));
      }, { passive: true });
    }
  }

  function initLightbox() {
    const box = $("[data-lightbox]");
    const triggers = $$("[data-expand]");
    if (!box || !triggers.length) return;
    document.body.appendChild(box); // escape any ancestor stacking context (e.g. sticky header)
    const img = $("[data-lightbox-img]", box);
    let lastFocus = null;

    const open = (el) => {
      const full = el.dataset.full || el.querySelector("img")?.src;
      if (!full) return; // e.g. hero currently showing a video, nothing to zoom
      img.src = full;
      img.alt = el.querySelector("img")?.alt || "";
      lastFocus = el;
      box.hidden = false;
      document.body.style.overflow = "hidden";
      $("[data-lightbox-close]", box).focus();
    };
    const close = () => {
      box.hidden = true;
      img.src = "";
      document.body.style.overflow = "";
      lastFocus?.focus();
    };

    triggers.forEach((el) => {
      el.addEventListener("click", () => open(el));
      el.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(el); }
      });
    });
    box.addEventListener("click", (e) => { if (e.target === box) close(); });
    $("[data-lightbox-close]", box).addEventListener("click", close);
    document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !box.hidden) close(); });
  }

  function initBuyBox() {
    const box = $("[data-buy-box]");
    if (!box) return;
    const variants = JSON.parse($("[data-variant-json]", box).textContent);

    // Qty clamp 1–9
    const qtyWrap = $("[data-qty]", box);
    const qtyInput = qtyWrap && $("input", qtyWrap);
    qtyWrap?.addEventListener("click", (e) => {
      const b = e.target.closest("button[data-step]");
      if (!b) return;
      qtyInput.value = Math.min(9, Math.max(1, (+qtyInput.value || 1) + +b.dataset.step));
    });
    qtyInput?.addEventListener("change", () => {
      qtyInput.value = Math.min(9, Math.max(1, +qtyInput.value || 1));
    });

    // Variant chips
    const groups = $$("[data-option-group]", box);
    const selected = groups.map((g) => $(".pdp-chip.is-selected", g)?.dataset.value);
    const notifyVariantChange = (v) => {
      const input = $("[data-variant-id]", box);
      const form = input?.closest("form");

      // App blocks such as Afterpay messaging usually listen for the native
      // variant input/form change rather than this theme's custom chip click.
      input?.dispatchEvent(new Event("input", { bubbles: true }));
      input?.dispatchEvent(new Event("change", { bubbles: true }));
      form?.dispatchEvent(new Event("change", { bubbles: true }));

      // Keep legacy/script-snippet integrations in step with the custom PDP.
      window.afterpay_current_variant = { id: v.id, price: v.price };
      document.dispatchEvent(new CustomEvent("variant:change", { detail: { variant: v } }));
      document.dispatchEvent(new CustomEvent("product:variant-change", { detail: { variant: v } }));
      document.dispatchEvent(new CustomEvent("pdp:variant", { detail: v }));
    };
    const render = (v) => {
      if (!v) return;
      $("[data-variant-id]", box).value = v.id;
      $("[data-price]", box).textContent = money(v.price);
      const cmp = $("[data-compare]", box), pill = $("[data-save-pill]", box);
      const onSale = v.compare_at_price > v.price;
      cmp.hidden = pill.hidden = !onSale;
      if (onSale) { cmp.textContent = money(v.compare_at_price); pill.textContent = "Save " + money(v.compare_at_price - v.price); }
      // The [data-instalments] line was removed from the buy box (Afterpay renders its own
      // messaging), and updating it here threw on every chip click, aborting the rest of render().
      const stock = $("[data-stock]", box);
      // Made-to-order products carry a fixed availability note instead of a
      // stock count; textContent since it comes from a metafield.
      const availNote = stock.dataset.availabilityNote;
      if (availNote) stock.textContent = availNote;
      else stock.innerHTML = v.available
        ? "In stock" + (v.inventory_managed && v.inventory_quantity > 0 && v.inventory_quantity < 10
            ? " · <strong>" + v.inventory_quantity + " left</strong>" : "")
        : "Out of stock";
      const atc = $("[data-atc]", box);
      atc.disabled = !v.available;
      atc.textContent = v.available ? "Add to cart" : "Sold out";
      showVariantMedia(v.media_id);
      notifyVariantChange(v);
    };
    groups.forEach((g, gi) =>
      g.addEventListener("click", (e) => {
        const chip = e.target.closest("[data-chip]");
        if (!chip) return;
        selected[gi] = chip.dataset.value;
        $$(".pdp-chip", g).forEach((c) => {
          c.classList.toggle("is-selected", c === chip);
        });
        render(variants.find((v) => v.options.every((o, i) => o === selected[i])));
      })
    );

    // Deep links (?variant=..., Google Shopping URLs) land on a specific variant: show its image too.
    const current = variants.find((x) => String(x.id) === $("[data-variant-id]", box).value);
    if (current) showVariantMedia(current.media_id);
  }

  function initPostcodeChecker() {
    const box = $("[data-postcode-checker]");
    if (!box) return;
    const zones = JSON.parse($("[data-postcode-zones]", box).textContent);
    const isMetro = (pc) => {
      const n = +pc;
      return zones.some((z) => {
        const [lo, hi] = z.split("-").map(Number);
        return hi ? n >= lo && n <= hi : n === lo;
      });
    };
    const input = $("[data-postcode-input]", box);
    const result = $("[data-postcode-result]", box);
    const check = () => {
      const pc = input.value.trim();
      result.classList.remove("is-quote");
      if (!/^\d{4}$/.test(pc)) {
        result.textContent = "Enter a valid 4-digit postcode.";
        return;
      }
      if (isMetro(pc)) {
        result.innerHTML = "<strong>" + money(+box.dataset.rateCents) + " " + box.dataset.rateName +
          "</strong> to " + pc + ".";
      } else {
        const chatUrl = box.dataset.chatUrl;
        result.classList.add("is-quote");
        result.innerHTML = "Outside our flat-rate delivery area — " +
          (chatUrl ? '<a class="pdp-link" href="' + chatUrl + '">request a delivery quote</a>' : "contact us for a delivery quote") +
          " and we&rsquo;ll confirm the cost before you pay.";
      }
    };
    $("[data-postcode-check]", box).addEventListener("click", check);
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); check(); }
    });
  }

  function initSticky() {
    const box = $("[data-buy-box]");
    const bar = $("[data-sticky-atc]");
    if (!box || !bar || !("IntersectionObserver" in window)) return;
    new IntersectionObserver(
      ([e]) => {
        const show = !e.isIntersecting && e.boundingClientRect.top < 0;
        bar.classList.toggle("is-visible", show);
        bar.setAttribute("aria-hidden", String(!show));
        // initStickyClearance listens for this to move app widgets out of the
        // bar's way (and put them back when it hides).
        document.dispatchEvent(new CustomEvent("pdp:sticky", { detail: { visible: show } }));
        const stickyBtn = $("[data-sticky-atc-btn]", bar);
        if (stickyBtn) {
          if (show) stickyBtn.removeAttribute("tabindex");
          else stickyBtn.tabIndex = -1;
        }
      },
      { threshold: 0 }
    ).observe(box);
    document.addEventListener("pdp:variant", (e) => {
      const p = $("[data-sticky-price]", bar);
      if (p) p.textContent = money(e.detail.price);
      const btn = $("[data-sticky-atc-btn]", bar);
      if (btn) {
        btn.disabled = !e.detail.available;
        btn.textContent = e.detail.available ? "Add to cart" : "Sold out";
      }
    });
  }

  function initChatTrigger() {
    // "Talk to an expert" opens the existing live-chat widget (Chatty/Avada
    // FAQ, [data-chatty-widget="trigger"]) instead of navigating to the
    // contact page, so a question doesn't cost the shopper the PDP they're
    // deciding on. Falls back to the plain href if the widget hasn't
    // loaded yet (third-party script, can be slow or blocked).
    $$("[data-pdp-chat-trigger]").forEach((el) => {
      el.addEventListener("click", (e) => {
        const widget = document.querySelector('[data-chatty-widget="trigger"]');
        if (!widget) return;
        e.preventDefault();
        widget.click();
      });
    });
  }

  /* Keep third-party bottom-anchored widgets off the sticky Add to cart bar.
     Every app that pins something to the bottom of the viewport does it with
     no knowledge of our bar, so they land on top of it. We fixed Avada's chat
     button by hand in pdp.css; a Klaviyo-style "$50 off" welcome-offer teaser
     then did the same thing, and the next app will too. Rather than keep
     adding one hard-coded selector per app, measure at runtime and nudge
     whatever actually overlaps.

     Only bottom-anchored elements are moved: an element positioned from the
     top has no `bottom` to adjust, and translating it could fight the app's
     own animations. Shopify's own preview bar and pixel iframes are skipped -
     the preview bar is staff-only chrome that no customer ever sees. */
  function initStickyClearance() {
    const bar = $("[data-sticky-atc]");
    if (!bar) return;

    const SKIP = /PBarNext|shopify-pc__|web-pixel|shopify-block/i;
    const GAP = 8;
    const MAX_NUDGE = 220; // a widget needing more than this is not a small pill
    const moved = new Map();

    const isSkipped = (el) => {
      const cls = el.className && el.className.baseVal !== undefined ? el.className.baseVal : el.className;
      return (el.id && SKIP.test(el.id)) || (cls && SKIP.test(String(cls)));
    };

    function restore() {
      for (const [el, prev] of moved) {
        if (prev === null) el.style.removeProperty("bottom");
        else el.style.setProperty("bottom", prev.value, prev.priority);
      }
      moved.clear();
    }

    function apply() {
      if (!bar.classList.contains("is-visible")) return restore();

      /* Measure where the bar RESTS, not where it is this frame. It slides in
         on a transform transition, so a getBoundingClientRect() taken when
         is-visible is first set still reports it off the bottom of the screen
         and nothing looks like it overlaps. Bottom offset plus height is
         transform-independent and gives the same answer at any point in the
         animation. */
      const b = bar.getBoundingClientRect();
      const barBottom = parseFloat(getComputedStyle(bar).bottom) || 0;
      const barH = bar.offsetHeight;
      if (!barH) return;
      const restTop = window.innerHeight - barBottom - barH;
      const restBottom = window.innerHeight - barBottom;
      const target = Math.ceil(barBottom + barH + GAP);

      for (const el of document.body.querySelectorAll("*")) {
        if (el === bar || bar.contains(el) || el.contains(bar) || isSkipped(el)) continue;

        const s = getComputedStyle(el);
        if (s.position !== "fixed" || s.bottom === "auto") continue;
        if (s.visibility === "hidden" || s.display === "none") continue;

        const r = el.getBoundingClientRect();
        if (r.width < 24 || r.height < 24) continue;
        if (r.top >= restBottom || r.bottom <= restTop) continue; // no vertical overlap
        if (r.right <= b.left || r.left >= b.right) continue; // no horizontal overlap (x is transform-free)
        if (target > MAX_NUDGE) continue;

        if (!moved.has(el)) {
          moved.set(el, el.style.bottom
            ? { value: el.style.bottom, priority: el.style.getPropertyPriority("bottom") }
            : null);
        }
        el.style.setProperty("bottom", target + "px", "important");
      }
    }

    // Widgets inject themselves late and on their own schedules, so re-check a
    // few times rather than once. Cheap: this runs on bar visibility changes
    // and resize, never on scroll.
    let timers = [];
    function sweep() {
      timers.forEach(clearTimeout);
      timers = [150, 800, 2500, 6000].map((ms) => setTimeout(apply, ms));
      apply();
    }

    document.addEventListener("pdp:sticky", sweep);
    window.addEventListener("resize", apply, { passive: true });
    sweep();
  }

  document.addEventListener("DOMContentLoaded", () => {
    initGallery();
    initLightbox();
    initBuyBox();
    initPostcodeChecker();
    initSticky();
    initChatTrigger();
    initStickyClearance();
  });
  window.__pdp = { money, $, $$ }; // shared helpers for appended modules
})();
